import React, { useMemo, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { matchKeywordCount } from './questionScoring';

// DischargeLetter question type. Student fills in a structured discharge
// medication summary (Continued / Stopped / New / Monitoring sections).
// Marking: per-section item-list match + keyword-presence on the
// monitoring narrative.

const DEFAULT_SECTIONS = [
  { key: 'continued', title: 'Continued medications', helper: 'Drugs the patient takes home unchanged.', mode: 'items' },
  { key: 'stopped', title: 'Stopped medications', helper: 'Drugs that should not be continued post-discharge.', mode: 'items' },
  { key: 'new', title: 'New medications', helper: 'Drugs newly started during admission to take home.', mode: 'items' },
  { key: 'monitoring', title: 'Monitoring + follow-up actions for GP', helper: 'Free text narrative — what the GP needs to monitor / arrange / act on.', mode: 'text' },
];

const DischargeLetter = ({ question }) => {
  const sections = useMemo(() => Array.isArray(question?.expectedSections) && question.expectedSections.length
    ? question.expectedSections
    : DEFAULT_SECTIONS, [question]);

  const [values, setValues] = useState(() => sections.reduce((acc, s) => ({ ...acc, [s.key]: '' }), {}));
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (key, value) => {
    setValues((curr) => ({ ...curr, [key]: value }));
  };

  // Score each section. For item-list sections: count how many expectedItems
  // appear in the student's text (substring match on first 2 words of each
  // expected item). For text sections: count rubricKeywords.
  const scoring = useMemo(() => {
    if (!submitted) return null;
    return sections.map((s) => {
      const studentText = String(values[s.key] || '').toLowerCase();
      if (s.mode === 'items') {
        const expectedItems = Array.isArray(s.expectedItems) ? s.expectedItems : [];
        const minimumCount = Number(s.minimumCount ?? Math.max(1, Math.ceil(expectedItems.length * 0.6)));
        let matches = 0;
        const matched = [];
        const missed = [];
        expectedItems.forEach((it) => {
          const needle = String(it).toLowerCase().split(/[\s(,]+/).slice(0, 2).join(' ').trim();
          if (needle && studentText.includes(needle)) {
            matches += 1;
            matched.push(it);
          } else {
            missed.push(it);
          }
        });
        return {
          key: s.key,
          title: s.title,
          mode: 'items',
          matches,
          minimumCount,
          expectedTotal: expectedItems.length,
          met: matches >= minimumCount,
          matched,
          missed,
        };
      }
      // text mode
      const keywords = Array.isArray(s.rubricKeywords) ? s.rubricKeywords : [];
      const minimumKeywords = Number(s.minimumKeywords ?? Math.max(1, Math.ceil(keywords.length * 0.6)));
      const hits = matchKeywordCount(studentText, keywords);
      return {
        key: s.key,
        title: s.title,
        mode: 'text',
        hits,
        minimumKeywords,
        keywordsTotal: keywords.length,
        met: hits >= minimumKeywords,
        keywords,
      };
    });
  }, [submitted, sections, values]);

  const overallMet = scoring ? scoring.every((s) => s.met) : false;
  const sectionsMet = scoring ? scoring.filter((s) => s.met).length : 0;

  const totalChars = Object.values(values).join('').replace(/\s+/g, '').length;
  const canSubmit = !submitted && totalChars >= 30;

  return (
    <Container className="mt-3 p-3 bg-light text-dark rounded discharge-letter-question">
      <h3 className="d-flex flex-wrap align-items-center gap-2">
        Q{question.questionNumber}: {question.questionTitle}
        {scoring ? (
          <Badge bg={overallMet ? 'success' : 'warning'} text={overallMet ? 'light' : 'dark'}>
            {sectionsMet} / {scoring.length} sections met
          </Badge>
        ) : null}
      </h3>
      <hr />
      <p className="mb-3">{question.questionText || 'Write the medication section of the discharge letter. Use the four standard sections below.'}</p>

      <div className="discharge-letter-question__sections">
        {sections.map((s) => (
          <Form.Group key={s.key} className="mb-3" controlId={`dl-${question.questionNumber}-${s.key}`}>
            <Form.Label className="mb-1"><strong>{s.title}</strong></Form.Label>
            <Form.Text className="d-block mb-1 text-muted">{s.helper}</Form.Text>
            <Form.Control
              as="textarea"
              rows={s.mode === 'text' ? 4 : 3}
              value={values[s.key]}
              disabled={submitted}
              onChange={(e) => handleChange(s.key, e.target.value)}
              placeholder={s.mode === 'items' ? 'List each drug + dose + frequency on a new line' : 'Free text...'}
            />
          </Form.Group>
        ))}
      </div>

      {scoring ? (
        <div className="discharge-letter-question__feedback">
          <h5 className="mt-2">Section breakdown</h5>
          <ul className="list-unstyled">
            {scoring.map((sec) => (
              <li key={sec.key} className="mb-2">
                <Badge bg={sec.met ? 'success' : 'warning'} text={sec.met ? 'light' : 'dark'} className="me-2">
                  {sec.met ? '✓' : '✗'} {sec.title}
                </Badge>
                {sec.mode === 'items' ? (
                  <span className="small text-muted">
                    {sec.matches} of {sec.expectedTotal} items captured (need ≥{sec.minimumCount}).
                    {sec.missed.length > 0 ? <span> Missed: {sec.missed.join('; ')}</span> : null}
                  </span>
                ) : (
                  <span className="small text-muted">
                    {sec.hits} of {sec.keywordsTotal} key-monitoring points covered (need ≥{sec.minimumKeywords}).
                    {sec.hits < sec.minimumKeywords ? <span> Look for: {sec.keywords.join(', ')}</span> : null}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {question.modelAnswer ? (
            <details className="mt-2">
              <summary><strong>Model discharge medication section</strong></summary>
              <div className="mt-2 p-3 bg-white rounded border" style={{ whiteSpace: 'pre-wrap' }}>{question.modelAnswer}</div>
            </details>
          ) : null}
        </div>
      ) : null}

      {!submitted ? (
        <Button
          type="button"
          variant="primary"
          className="mt-3"
          disabled={!canSubmit}
          onClick={() => setSubmitted(true)}
        >
          Submit discharge letter
        </Button>
      ) : null}
    </Container>
  );
};

export default DischargeLetter;
